import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import Layout from '../../components/Layout';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Button,
  Alert,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import { useMst } from '../../model/Root';
import { useTranslation } from 'react-i18next';
import {
  createSubscriptionCodes,
  fetchSubscriptionCodesByProductId,
  SubscriptionCodeItem,
} from '../../services/subscriptionCodeApi';

type SubscriptionTypeKey = 'Basic' | 'Couple' | 'Student' | 'Family' | 'Gift';

const TYPE_TO_PREFIX: Record<SubscriptionTypeKey, string> = {
  Basic: '1P',
  Couple: '2P',
  Student: '1O',
  Family: '5P',
  Gift: 'A',
};

const MONTH_OPTIONS = [1, 3, 6, 12];

interface SubscriptionCodesScreenProps {
  standalone?: boolean;
}

const SubscriptionCodesScreen: React.FC<SubscriptionCodesScreenProps> = observer(({ standalone = true }) => {
  const { t } = useTranslation();
  const {
    errorStore: { setError, setSuccess },
    userStore: { user },
  } = useMst();

  const [type, setType] = useState<SubscriptionTypeKey>('Basic');
  const [months, setMonths] = useState<number>(12);
  const [count, setCount] = useState<number>(10);
  const [prefix, setPrefix] = useState<string>(TYPE_TO_PREFIX['Basic']);
  const [prefixDirty, setPrefixDirty] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [codes, setCodes] = useState<SubscriptionCodeItem[]>([]);
  const [requestedCount, setRequestedCount] = useState<number>(0);
  const [currentBatchId, setCurrentBatchId] = useState<string | null>(null);

  const generatedDefaultProductId = useMemo(() => {
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    const ts = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(
      now.getMinutes()
    )}${pad(now.getSeconds())}`;
    return `manual_${type.toLowerCase()}_${months}m_${ts}`;
  }, [type, months]);

  const exampleCode = useMemo(() => `${prefix}-ABCDE`, [prefix]);

  const computeDefaultPrefix = (subscriptionType: SubscriptionTypeKey, selectedMonths: number): string => {
    const base = TYPE_TO_PREFIX[subscriptionType];
    if (subscriptionType === 'Gift') {
      // Gifts: A + months (A1, A3, A6, A12). If months unrecognized, just A
      if ([1, 3, 6, 12].includes(selectedMonths)) return `${base}${selectedMonths}`;
      return base;
    }
    switch (selectedMonths) {
      case 12:
        return `${base}V`;
      case 1:
        return `${base}K`;
      case 3:
        return `${base}3K`;
      case 6:
        return `${base}6K`;
      default:
        return base;
    }
  };

  // Reset batch if type or months change (new default productId)
  useEffect(() => {
    setCurrentBatchId(null);
    setRequestedCount(0);
    setCodes([]);
  }, [type, months]);

  // Auto-fill prefix from type + months when user has not manually edited it
  useEffect(() => {
    if (!prefixDirty) {
      setPrefix(computeDefaultPrefix(type, months));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, months]);

  const validate = (): boolean => {
    if (!months || months <= 0) {
      setError(t('subscription_codes.invalid_months'));
      return false;
    }
    if (!count || count <= 0) {
      setError(t('subscription_codes.invalid_count'));
      return false;
    }
    if (!prefix || prefix.length === 0) {
      setError(t('subscription_codes.invalid_prefix'));
      return false;
    }
    return true;
  };

  const onGenerate = async () => {
    if (!validate()) return;
    try {
      setLoading(true);
      const batchId = currentBatchId ?? generatedDefaultProductId;
      const isSameBatch = currentBatchId === batchId;
      setCurrentBatchId(batchId);
      setRequestedCount((prev) => (isSameBatch ? prev + count : count));
      await createSubscriptionCodes({ prefix, productId: batchId, months, count });
      const batch = await fetchSubscriptionCodesByProductId(batchId);
      setCodes(batch);
      if (batch.length < count) {
        setError(t('subscription_codes.partial_warning', { requested: count, received: batch.length }));
      } else {
        setSuccess(t('subscription_codes.generated_success', { count: batch.length }));
      }
    } catch (e) {
      setError(t('subscription_codes.generate_failed'));
    } finally {
      setLoading(false);
    }
  };

  const onTopUp = async () => {
    if (!validate()) return;
    try {
      setLoading(true);
      const batchId = currentBatchId ?? generatedDefaultProductId;
      const deficit = Math.max(0, requestedCount - codes.length);
      if (deficit <= 0) return;
      await createSubscriptionCodes({ prefix, productId: batchId, months, count: deficit });
      const batch = await fetchSubscriptionCodesByProductId(batchId);
      setCodes(batch);
    } catch (e) {
      setError(t('subscription_codes.generate_failed'));
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      const text = codes.map((c) => c.code).join('\n');
      await navigator.clipboard.writeText(text);
      setSuccess(t('subscription_codes.copy_success'));
    } catch (e) {
      setError(t('subscription_codes.copy_failed'));
    }
  };

  const downloadTxt = () => {
    const blob = new Blob([codes.map((c) => c.code).join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const base = (currentBatchId || generatedDefaultProductId).replace(/[^a-zA-Z0-9_-]/g, '_');
    a.download = `${base}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadCsv = () => {
    const header = 'code,months,used\n';
    const rows = codes.map((c) => `${c.code},${c.months},${c.used ? 'true' : 'false'}`).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const base = (currentBatchId || generatedDefaultProductId).replace(/[^a-zA-Z0-9_-]/g, '_');
    a.download = `${base}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const isAdmin = !!user?.admin;

  const content = (
    <>
      {!isAdmin && <Alert severity="error">{t('subscription_codes.admin_only')}</Alert>}
      <Box p={3}>
        <Card>
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  select
                  label={t('subscription_codes.type')}
                  fullWidth
                  value={type}
                  onChange={(e) => {
                    const next = e.target.value as SubscriptionTypeKey;
                    setType(next);
                    setPrefixDirty(false);
                    setPrefix(computeDefaultPrefix(next, months));
                  }}
                >
                  {Object.keys(TYPE_TO_PREFIX).map((key) => (
                    <MenuItem key={key} value={key}>
                      {t(`subscription_codes.type_${key.toLowerCase()}`)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  select
                  label={t('subscription_codes.months')}
                  fullWidth
                  value={months}
                  onChange={(e) => setMonths(Number(e.target.value))}
                >
                  {MONTH_OPTIONS.map((m) => (
                    <MenuItem key={m} value={m}>
                      {m}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  type="number"
                  label={t('subscription_codes.count')}
                  fullWidth
                  value={count}
                  inputProps={{ min: 1 }}
                  onChange={(e) => setCount(Math.max(1, Number(e.target.value)))}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label={t('subscription_codes.prefix')}
                  fullWidth
                  value={prefix}
                  onChange={(e) => {
                    setPrefix(e.target.value);
                    setPrefixDirty(true);
                  }}
                  helperText={`${t('subscription_codes.example')}: ${exampleCode}`}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Button variant="contained" onClick={onGenerate} disabled={loading || !isAdmin}>
                    {t('subscription_codes.generate')}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={onTopUp}
                    disabled={loading || !isAdmin || codes.length >= requestedCount || requestedCount === 0}
                  >
                    {t('subscription_codes.top_up')}
                  </Button>
                  <Divider orientation="vertical" flexItem />
                  <Button variant="outlined" onClick={copyToClipboard} disabled={codes.length === 0}>
                    {t('subscription_codes.copy')}
                  </Button>
                  <Button variant="outlined" onClick={downloadTxt} disabled={codes.length === 0}>
                    {t('subscription_codes.download_txt')}
                  </Button>
                  <Button variant="outlined" onClick={downloadCsv} disabled={codes.length === 0}>
                    {t('subscription_codes.download_csv')}
                  </Button>
                  {requestedCount > 0 && (
                    <Chip
                      color={codes.length < requestedCount ? 'warning' : 'success'}
                      label={`${t('subscription_codes.generated')}: ${codes.length}/${requestedCount}`}
                    />
                  )}
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box mt={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('subscription_codes.results')}
              </Typography>
              {codes.length === 0 ? (
                <Alert severity="info">{t('subscription_codes.no_results')}</Alert>
              ) : (
                <Box sx={{ maxHeight: 400, overflowY: 'auto', border: '1px solid #eee', borderRadius: 1, p: 2 }}>
                  {codes.map((c) => (
                    <Typography key={c.code} variant="body2">
                      {c.code}
                      {c.used && (
                        <Chip size="small" color="default" label={t('subscription_codes.used')} sx={{ ml: 1 }} />
                      )}
                    </Typography>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>
    </>
  );
  if (standalone) {
    return <Layout title={t('subscription_codes.title')}>{content}</Layout>;
  }
  return content;
});

export default SubscriptionCodesScreen;
